import { leftMetrics, lowestDdpoPost, parseNumeric } from "./excelHelpers";

// Given the left-half rows (after handicap, sorted by DDPO ascending),
// build the Exacta + Trifecta groups per the user's rules.
//
//   Exacta   (min 6 horses)
//     Front  = top-3 DDPO + lowest-DD horse not already in top-3   → 4 horses
//     Back   = 3 lowest DD horses overall                          → 3 horses
//     Combos = valid ordered pairs (a,b), a ∈ Front, b ∈ Back, a≠b
//     Min    = $1.00
//
//   Trifecta — "1 Key + 5 Key Box"  (min 3 horses, target 6)
//     Key    = top-ranked DDPO (after eliminations)
//     With   = next 5 by DDPO rank (after eliminations)
//     Hit    = key finishes in top-3 AND the other two top-3 are
//              both in With.
//     Combos = 3 (positions for the key) × |With| × (|With|-1)
//     Min    = $0.50

const EXACTA_MIN_FIELD = 6;
const TRIFECTA_KB_MIN_FIELD = 3; // 1 key + 2 with minimum
export const TRIFECTA_KB_SIZES = [3, 4, 5]; // "with" sizes available in the UI
export const TRIFECTA_KB_DEFAULT_SIZE = 5;
export const EXOTIC_MAX_FIELD = 10;
export const EXACTA_MIN_BASE = 1;
export const TRIFECTA_MIN_BASE = 0.5;

const validLeftRows = (leftRows, elimSet) => {
  const isElim = (r) =>
    elimSet instanceof Set && elimSet.has(Number(r.S));
  const m = leftMetrics(leftRows);
  return leftRows.filter((r, i) => m[i].iq !== null && !isElim(r));
};

// Ordered pairs (a,b), a ∈ A, b ∈ B, a ≠ b.
export const countExactaCombos = (front, back) => {
  let n = 0;
  for (const a of front) for (const b of back) if (a !== b) n += 1;
  return n;
};

// Key Box Trifecta: key must land in top-3; the other two top-3 slots
// come from `withPosts` (distinct). Combos = 3 * |with| * (|with|-1).
export const countKeyBoxCombos = (withPosts) => {
  const n = withPosts.length;
  if (n < 2) return 0;
  return 3 * n * (n - 1);
};

// After handicap, valid rows are already sorted by IQ Power ascending, so the
// first N of `valid` give us the top-N DDPO ranks.
export const buildExoticGroups = (
  leftRows,
  elimSet,
  triWithSize = TRIFECTA_KB_DEFAULT_SIZE,
  keyPostOverride = null,
) => {
  const valid = validLeftRows(leftRows, elimSet);
  const fieldSize = valid.length;

  const topDDPO3 = valid.slice(0, 3).map((r) => Number(r.S));

  const byDD = valid
    .slice()
    .sort((a, b) => (parseNumeric(a.DD) ?? Infinity) - (parseNumeric(b.DD) ?? Infinity))
    .map((r) => Number(r.S));

  // Exacta Front: top-3 DDPO + lowest-DD horse not already in top-3.
  const extra = byDD.find((s) => !topDDPO3.includes(s));
  const front =
    extra !== undefined && topDDPO3.length === 3
      ? [...topDDPO3, extra]
      : topDDPO3.slice();

  const lowest3DD = byDD.slice(0, 3);

  const tooLarge = fieldSize > EXOTIC_MAX_FIELD;
  const exactaAvailable = !tooLarge && fieldSize >= EXACTA_MIN_FIELD;

  // Trifecta Key Box groups (DDPO rank order).
  // If the user has locked a specific horse as the Key, that post
  // becomes the Trifecta Key and the "with" list is the top-ranked
  // DDPO horses after the key (preserving the "N with" size the user
  // picked). Falls back to top-DDPO default when no key is locked.
  const byDDPO = valid.map((r) => Number(r.S));
  const overrideNum =
    keyPostOverride != null && Number.isFinite(Number(keyPostOverride))
      ? Number(keyPostOverride)
      : null;
  const overrideInField =
    overrideNum != null && byDDPO.includes(overrideNum) ? overrideNum : null;
  // Default Key = lowest DDPO regardless of IQ rank; no will pays → #1 IQ Power.
  const triKey = overrideInField ?? lowestDdpoPost(valid) ?? byDDPO[0] ?? null;
  const otherPosts = byDDPO.filter((p) => p !== triKey);
  const triWith = otherPosts.slice(0, Math.max(2, triWithSize));
  const trifectaAvailable =
    !tooLarge && triKey !== null && triWith.length >= 2;

  const exactaCombos = exactaAvailable
    ? countExactaCombos(front, lowest3DD)
    : 0;
  const trifectaCombos = trifectaAvailable
    ? countKeyBoxCombos(triWith)
    : 0;

  return {
    fieldSize,
    tooLarge,
    exoticMaxField: EXOTIC_MAX_FIELD,
    front,
    lowest3DD,
    exactaAvailable,
    exactaMinField: EXACTA_MIN_FIELD,
    exactaCombos,
    trifectaAvailable,
    trifectaMinField: TRIFECTA_KB_MIN_FIELD,
    trifectaCombos,
    triKey,
    triWith,
    triKeyOverridden: overrideInField != null,
  };
};
